// Tailwind (preflight included) and the legacy stylesheet, in that order.
// eslint-disable-next-line import/order
import '@/styles/css/main.css';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { SpeedInsights } from '@vercel/speed-insights/next';
import Head from 'next/head';
import Script from 'next/script';
import { useMemo, useState } from 'react';
import { jsonLdScriptProps } from 'react-schemaorg';
import type { ImageObject } from 'schema-dts';
import { fallbackSettings } from '@/api/setup/setup-frontend';
import { getAuthToken } from '@/auth';
import { AppContainer } from '@/components/AppContainer';
import { JotaiDevTools } from '@/components/JotaiDevTools';
import { NewVersionPrompt } from '@/components/NewVersionPrompt';
import { AccountModals, AccountModalsProvider } from '@/components/Modals/Account';
import GlobalDownloadModal from '@/components/Modals/ConfirmDownload/GlobalDownloadModal';
import { FairUsageCheckpointModal } from '@/components/Modals/FairUsageCheckpoint';
import { StripeCheckoutResponseHandler } from '@/components/StripeCheckoutResponseHandler';
import VRTBridge from '@/components/VRTBridge';
import { Tracking } from '@/components/Tracking';
import { Toaster } from '@music-vine/cadence/ui';
import { VercelToolbar } from '@/components/VercelToolbar';
import { VideoFromQuery } from '@/components/Video/VideoFromQuery';
import { VideoPlayerWindow } from '@/features/VideoPlayer';
import { getUniversalCookie } from '@/cookies';
import { withDeliveredHydrationTimestamps } from '@/utils/rebase-hydrated-state';
import { useAnalyticsRecovery } from '@/hooks/analytics/use-analytics-recovery';
import { useIsClient } from '@/hooks/ui/useIsClient';
import { ABProvider } from '@/providers/ABProvider';
import { AppStateProvider } from '@/providers/AppStateProvider';
import { AuthHydrationProvider } from '@/providers/AuthHydrationProvider';
import { ClassProvider } from '@/providers/ClassProvider';
import { FileFormatProvider } from '@/providers/FileFormatProvider';
import { LocalSettingsProvider } from '@/providers/LocalSettingsProvider';
import { PortalProvider } from '@/providers/PortalProvider';
import { ReferralAlertsProvider } from '@/providers/ReferralAlertsProvider';
import { SettingsProvider } from '@/providers/SettingsProvider';
import { SidebarProvider } from '@/providers/SidebarProvider';
import { StripeOverlayProvider } from '@/providers/StripeOverlayProvider';
import { ThemeProvider } from '@/providers/ThemeProvider';
import { TrackingProvider } from '@/providers/TrackingProvider';
import { PagesProgressProvider as ProgressProvider } from '@bprogress/next';
import type { Settings } from '@/types/settings';
import type { AppProps } from 'next/app';
import { defaultOpenGraphImage } from '@/components/SEO';
import { BoardsContextProvider } from '@/features/Boards/context-provider';
import { AppErrorBoundary } from '@/components/AppErrorBoundary';
import { BoardsToaster } from '@/features/Boards/toaster';
import { ReferralsProvider } from '@/providers/ReferralsProvider';
import { useRouter } from 'next/router';

// Global ImageObject schema constants (DEV-4498)
// Note: This uses a different image than defaultOpenGraphImage as per requirement
// to help Google better identify a representative image for search results
const GLOBAL_IMAGE_OBJECT_URL = 'https://cdn.uppbeat.io/og_3.jpg';
const GLOBAL_IMAGE_OBJECT_NAME = 'Uppbeat - Royalty-Free Music For Creators';
const GLOBAL_IMAGE_OBJECT_DESCRIPTION = 'Uppbeat platform interface showing copyright-free music for content creators';
const getInitialSettings = () => {
  try {
    const userSettings = localStorage.getItem('settings');
    const initialSettings: Settings = userSettings ? JSON.parse(userSettings) : fallbackSettings;
    const authToken = getAuthToken();
    if (initialSettings.user) {
      initialSettings.user.auth_token = authToken;
    }
    return {
      initialSettings
    };
  } catch {
    return {};
  }
};

// Next.js exposes the catch-all route filename via `router.pathname`. Kept
// as a named constant so a file rename surfaces here at type-check time
// rather than silently restoring the chrome on /login.
const LOGIN_PATHNAME = '/login/[[...request]]';
const ENV_AUTH_LOGIN_PATHNAME = '/env-auth/login';
// The reset-password token sits in the URL path; render it chromeless so the
// marketing/analytics stack (<Tracking />) never mounts and captures the token
// via document.URL (GA page_location, Meta PageView, Clarity replay) — DEV-6138.
const RESET_PASSWORD_PATHNAME = '/reset-password/confirm/[hash]';
const createQueryClient = () => new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false
    }
  }
});

// On the client, hold a single QueryClient across page navigations.
// On the server, `getQueryClient` always returns a fresh client so that one
// request can never leak cache state into the next on a warm Vercel function
// instance — the bug behind the DEV-5788 pricing flash, where a prior
// request's prefetched data made HydrationBoundary skip server-side
// hydration (TanStack Query v5 defers existing-query hydration to useEffect,
// which never fires during SSR).
let browserQueryClient: QueryClient | undefined;
const getQueryClient = () => {
  if (typeof window === 'undefined') {
    return createQueryClient();
  }
  browserQueryClient ??= createQueryClient();
  return browserQueryClient;
};
const App = ({
  Component,
  pageProps: deliveredPageProps
}: AppProps) => {
  // useState lazy-initialiser ensures this runs once per App mount: once per
  // request on the server, and exactly once per browser session on the client.
  // Avoids the module-level singleton that was causing cross-request cache
  // leakage on serverless functions (see comment on `getQueryClient`).
  const [queryClient] = useState(getQueryClient);

  // A page's `dehydratedState` carries the timestamps of the server render that
  // produced it, which for a CDN-cached document can be an hour old by the time
  // a browser hydrates it. Age it from delivery instead, so listings don't
  // refetch on mount the rows they were just handed (DEV-6490). Memoised on the
  // props object Next hands us — one new identity per navigation — because
  // HydrationBoundary re-hydrates whenever its state changes identity.
  const pageProps = useMemo(() => typeof window === 'undefined' ? deliveredPageProps : withDeliveredHydrationTimestamps(deliveredPageProps), [deliveredPageProps]);
  const {
    initialSettings
  } = getInitialSettings();

  // Initialize analytics recovery system for critical events
  useAnalyticsRecovery();
  const {
    asPath,
    pathname: routePathname
  } = useRouter();
  // The env-auth login is a hard gate: render it without the app chrome
  // (sidebar, top bar, modals, audio player) so unauthenticated visitors
  // can't interact with anything else on the page.
  const isEnvAuthLogin = routePathname === ENV_AUTH_LOGIN_PATHNAME;
  // The /login page is a full-screen takeover landing.
  const isLoginPage = routePathname === LOGIN_PATHNAME;
  const isResetPasswordPage = routePathname === RESET_PASSWORD_PATHNAME;
  const isChromelessAuthPage = isEnvAuthLogin || isLoginPage || isResetPasswordPage;
  const isClient = useIsClient();
  const canonicalLink = useMemo(() => {
    let pathname = asPath.split('?')[0];
    if (pathname === '/index') {
      pathname = '/';
    }

    // Force lowercase to prevent issues like "VHS"
    pathname = pathname.toLowerCase();
    return new URL(pathname, process.env.NEXT_PUBLIC_FE_BASE_URL ?? null).href;
  }, [asPath]);
  if (!isClient && process.env.NEXT_PUBLIC_MAINTENANCE_MODE === 'true') {
    return null;
  }
  const bypassMaintenance = getUniversalCookie('BYPASS_MAINTENANCE');

  // Disable normal app if we're in maintenance mode.
  if (process.env.NEXT_PUBLIC_MAINTENANCE_MODE === 'true' && bypassMaintenance !== process.env.NEXT_PUBLIC_MAINTENANCE_BYPASS_COOKIE_VALUE) {
    return <>
        <script dangerouslySetInnerHTML={{
        __html: `(function(){try{const theme=localStorage.getItem('theme');const prefersDarkMode=window.matchMedia('(prefers-color-scheme: dark)',).matches;
        if(!theme){if(prefersDarkMode){document.documentElement.classList.add('theme-dark')}else{document.documentElement.classList.add('theme-light')} return}
        document.documentElement.classList.add('theme-'+theme)}catch(e){}})()`
      }} id="initial-theme" />
        <QueryClientProvider client={queryClient}>
          <Component {...pageProps} />
        </QueryClientProvider>
      </>;
  }
  return <>
      <Head data-sentry-element="Head" data-sentry-source-file="_app.page.tsx">
        <meta charSet="utf-8" data-sentry-element="meta" data-sentry-source-file="_app.page.tsx" />
        <meta content="#F23D75" name="theme-color" data-sentry-element="meta" data-sentry-source-file="_app.page.tsx" />

        <meta content="width=device-width, height=device-height, initial-scale=1.0" name="viewport" data-sentry-element="meta" data-sentry-source-file="_app.page.tsx" />
        <meta content="Download the best copyright free music from the world's hottest beatmakers and indie artists. Create your free account & start downloading now." name="description" data-sentry-element="meta" data-sentry-source-file="_app.page.tsx" />

        {/* Prevent indexing on non-production environments. The former
            NEXT_PUBLIC_ALLOW_INDEXING escape hatch was set on preprod, which
            is how it ended up crawled and linked in Search Console
            (DEV-6477). NEXT_PUBLIC_SEO_TEST_CRAWL replaces it and must only
            ever be set on a single branch-scoped Preview deployment. */}
        {process.env.NEXT_PUBLIC_SEO_TEST_CRAWL !== 'true' && (process.env.NEXT_PUBLIC_ENV !== 'production' || !!process.env.NEXT_PUBLIC_FE_BASE_URL && !process.env.NEXT_PUBLIC_FE_BASE_URL.toLowerCase().includes('uppbeat.io')) && <meta content="noindex, nofollow" name="robots" />}

        <meta content="website" property="og:type" data-sentry-element="meta" data-sentry-source-file="_app.page.tsx" />
        <meta content="141409067700997" property="fb:app_id" data-sentry-element="meta" data-sentry-source-file="_app.page.tsx" />
        <meta content="Royalty-Free Music For Creators" key="og:title" property="og:title" data-sentry-element="meta" data-sentry-source-file="_app.page.tsx" />
        <meta content="Create your free Uppbeat account & start downloading the best copyright-free music from the world's hottest beatmakers." key="og:description" property="og:description" data-sentry-element="meta" data-sentry-source-file="_app.page.tsx" />
        {/* Keyed so a page's <SEO og={{ image }}> replaces it; no width/height
            hints, as they'd be wrong for page-specific images. */}
        <meta content={defaultOpenGraphImage} key="og:image" property="og:image" data-sentry-element="meta" data-sentry-source-file="_app.page.tsx" />
        <meta content="https://uppbeat.io/" property="og:url" data-sentry-element="meta" data-sentry-source-file="_app.page.tsx" />

        <meta content="summary_large_image" name="twitter:card" data-sentry-element="meta" data-sentry-source-file="_app.page.tsx" />
        <meta content="@UppbeatOfficial" name="twitter:site" data-sentry-element="meta" data-sentry-source-file="_app.page.tsx" />
        <meta content="Royalty-Free Music For Creators" key="twitter:title" name="twitter:title" data-sentry-element="meta" data-sentry-source-file="_app.page.tsx" />
        <meta content="Create your free Uppbeat account & start downloading the best copyright-free music from the world's hottest beatmakers." key="twitter:description" name="twitter:description" data-sentry-element="meta" data-sentry-source-file="_app.page.tsx" />
        <meta content={defaultOpenGraphImage} key="twitter:image" name="twitter:image" data-sentry-element="meta" data-sentry-source-file="_app.page.tsx" />
        <meta content="Uppbeat" name="twitter:image:alt" data-sentry-element="meta" data-sentry-source-file="_app.page.tsx" />

        <title key="title">
          Free Music For YouTube Videos & Creators • Uppbeat
        </title>

        {!!canonicalLink && <link href={canonicalLink} key="canonical" rel="canonical" />}

        {/* Global ImageObject schema for all pages (DEV-4498)
            Helps Google identify the representative image for search results.
            Individual pages may also include additional, more specific ImageObject schemas. */}
        <script {...jsonLdScriptProps<ImageObject>({
        '@context': 'https://schema.org',
        '@type': 'ImageObject',
        url: GLOBAL_IMAGE_OBJECT_URL,
        contentUrl: GLOBAL_IMAGE_OBJECT_URL,
        name: GLOBAL_IMAGE_OBJECT_NAME,
        description: GLOBAL_IMAGE_OBJECT_DESCRIPTION
      })} />
      </Head>
      <SpeedInsights sampleRate={0.1} data-sentry-element="SpeedInsights" data-sentry-source-file="_app.page.tsx" />

      {/* Polyfill for container query */}
      {!!isClient && !CSS.supports('container-type: inline-size') && <Script src="https://cdn.jsdelivr.net/npm/container-query-polyfill@1/dist/container-query-polyfill.modern.js" />}

      <AppStateProvider queryClient={queryClient} data-sentry-element="AppStateProvider" data-sentry-source-file="_app.page.tsx">
        {!!isClient && <JotaiDevTools />}
        <ProgressProvider color="#F23D75" height="2px" options={{
        showSpinner: false
      }} shallowRouting data-sentry-element="ProgressProvider" data-sentry-source-file="_app.page.tsx">
          <AppErrorBoundary data-sentry-element="AppErrorBoundary" data-sentry-source-file="_app.page.tsx">
            <LocalSettingsProvider data-sentry-element="LocalSettingsProvider" data-sentry-source-file="_app.page.tsx">
              <SettingsProvider initialSettings={initialSettings} data-sentry-element="SettingsProvider" data-sentry-source-file="_app.page.tsx">
                <StripeOverlayProvider data-sentry-element="StripeOverlayProvider" data-sentry-source-file="_app.page.tsx">
                  <SidebarProvider data-sentry-element="SidebarProvider" data-sentry-source-file="_app.page.tsx">
                    <ThemeProvider data-sentry-element="ThemeProvider" data-sentry-source-file="_app.page.tsx">
                      <PortalProvider data-sentry-element="PortalProvider" data-sentry-source-file="_app.page.tsx">
                        <ClassProvider data-sentry-element="ClassProvider" data-sentry-source-file="_app.page.tsx">
                          <TrackingProvider data-sentry-element="TrackingProvider" data-sentry-source-file="_app.page.tsx">
                            <ABProvider data-sentry-element="ABProvider" data-sentry-source-file="_app.page.tsx">
                              <AccountModalsProvider data-sentry-element="AccountModalsProvider" data-sentry-source-file="_app.page.tsx">
                                <FileFormatProvider data-sentry-element="FileFormatProvider" data-sentry-source-file="_app.page.tsx">
                                  <ReferralsProvider data-sentry-element="ReferralsProvider" data-sentry-source-file="_app.page.tsx">
                                    <ReferralAlertsProvider data-sentry-element="ReferralAlertsProvider" data-sentry-source-file="_app.page.tsx">
                                      <BoardsContextProvider data-sentry-element="BoardsContextProvider" data-sentry-source-file="_app.page.tsx">
                                        <FairUsageCheckpointModal data-sentry-element="FairUsageCheckpointModal" data-sentry-source-file="_app.page.tsx" />
                                        {/* One `GoogleOAuthProvider` for the
                                            whole app. It appends its own
                                            `accounts.google.com/gsi/client`
                                            tag on mount and strips it on
                                            unmount, so a provider per branch
                                            re-fetched Google Sign-In every
                                            time a user crossed the chromeless
                                            boundary. Env-auth stays outside —
                                            it has no Google button, so it
                                            shouldn't pay for the script. */}
                                        {isEnvAuthLogin ? <Component {...(({
                                        __auth: _,
                                        ...rest
                                      }) => rest)(pageProps)} /> : <GoogleOAuthProvider clientId={process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID}>
                                            {isChromelessAuthPage ? <AuthHydrationProvider auth={pageProps.__auth}>
                                                <Component {...(({
                                            __auth: _,
                                            ...rest
                                          }) => rest)(pageProps)} />
                                                <AccountModals />
                                                <GlobalDownloadModal />
                                                <Toaster />
                                              </AuthHydrationProvider> : <AppContainer>
                                                <Tracking />

                                                <AuthHydrationProvider auth={pageProps.__auth}>
                                                  <Component {...(({
                                              __auth: _,
                                              ...rest
                                            }) => rest)(pageProps)} />
                                                </AuthHydrationProvider>

                                                <AccountModals />
                                                <GlobalDownloadModal />
                                                <VRTBridge />
                                                <Toaster />
                                                <BoardsToaster />
                                                <NewVersionPrompt />
                                                <StripeCheckoutResponseHandler />
                                              </AppContainer>}
                                          </GoogleOAuthProvider>}
                                      </BoardsContextProvider>
                                    </ReferralAlertsProvider>
                                  </ReferralsProvider>
                                </FileFormatProvider>
                              </AccountModalsProvider>
                            </ABProvider>
                          </TrackingProvider>
                        </ClassProvider>
                      </PortalProvider>
                    </ThemeProvider>
                  </SidebarProvider>
                </StripeOverlayProvider>
              </SettingsProvider>
            </LocalSettingsProvider>
            <VercelToolbar data-sentry-element="VercelToolbar" data-sentry-source-file="_app.page.tsx" />
            <VideoPlayerWindow data-sentry-element="VideoPlayerWindow" data-sentry-source-file="_app.page.tsx" />
          </AppErrorBoundary>
        </ProgressProvider>
      </AppStateProvider>
      <VideoFromQuery data-sentry-element="VideoFromQuery" data-sentry-source-file="_app.page.tsx" />
    </>;
};
export default App;